import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import { useAuth } from "../../context/AuthContext";
import api from "../../services/api";
import { formatPlanning } from "utils/planning";

const MATIERES = {
  quoran: { label: "Qour'an", color: "#2e7d32", bg: "#e8f5e9" },
  arabe: { label: "Langue arabe", color: "#1565c0", bg: "#e3f2fd" },
  science: { label: "Sciences de la religion", color: "#e65100", bg: "#fff3e0" },
};

function getLast12Months() {
  const months = [];
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("fr-FR", { month: "short" });
    months.push({ key, label });
  }
  return months;
}

function getCurrentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

function getCurrentMonthLabel() {
  return new Date().toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
}

function Dashboard() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState({ eleves: 0, enseignants: 0, cours: 0, livres: 0 });
  const [objectifs, setObjectifs] = useState(null);
  const [elevesData, setElevesData] = useState([]);
  const [enseignantsData, setEnseignantsData] = useState([]);
  const [derniersCours, setDerniersCours] = useState([]);
  const [elevesParMois, setElevesParMois] = useState({ labels: [], datasets: { label: "", data: [] } });
  const [coursParMois, setCoursParMois] = useState({ labels: [], datasets: { label: "", data: [] } });
  const [elevesSansCours, setElevesSansCours] = useState([]);
  const [activiteEnseignants, setActiviteEnseignants] = useState([]);
  const [nouveauxEleves, setNouveauxEleves] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  const calcRealise = (items, saison) => {
    const debut = new Date(saison, 6, 1);
    const finExclusive = new Date(saison + 1, 6, 1);
    return items.filter((item) => {
      if (!item.createdAt) return false;
      const d = new Date(item.createdAt);
      return d >= debut && d < finExclusive;
    }).length;
  };

  const fetchData = async () => {
    try {
      const promises = [
        api.get("/eleves"),
        api.get("/cours?tous=true"),
        api.get("/livres"),
        api.get("/eleves/stats/par-mois"),
        api.get("/cours/stats/par-mois"),
      ];
      if (isAdmin) promises.push(api.get("/enseignants"));
      if (isAdmin) {
        api.get("/objectifs/mien")
          .then((r) => { if (r.data.defini) setObjectifs(r.data); })
          .catch(() => {});
      }

      const results = await Promise.all(promises);
      const [elevesRes, coursRes, livresRes, elevesStats, coursStats] = results;
      const enseignantsRes = isAdmin ? results[5] : { data: [] };

      setStats({
        eleves: elevesRes.data.length,
        enseignants: enseignantsRes.data.length,
        cours: coursRes.data.filter((c) => !c.chapitre_termine && c.type !== "general").length,
        livres: livresRes.data.length,
      });
      setElevesData(elevesRes.data);
      setEnseignantsData(enseignantsRes.data);

      const sorted = [...coursRes.data].sort((a, b) =>
        new Date(b.createdAt) - new Date(a.createdAt)
      );
      setDerniersCours(sorted.slice(0, 5));

      // ── NOUVEAUX ÉLÈVES DU MOIS ──
      const currentMonth = getCurrentMonthKey();
      const nouveaux = elevesRes.data.filter((e) => {
        if (!e.createdAt) return false;
        return e.createdAt.slice(0, 7) === currentMonth;
      }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setNouveauxEleves(nouveaux);

      // ── ÉLÈVES SANS COURS ──
      const elevesAvecCoursDirects = new Set(coursRes.data.map((c) => c.eleve_id).filter(Boolean));
      const elevesAvecCoursGroupe = new Set();
      coursRes.data.forEach((c) => {
        if (c.groupe?.eleves) {
          c.groupe.eleves.forEach((e) => elevesAvecCoursGroupe.add(e.id));
        }
      });
      const tousElevesAvecCours = new Set([...elevesAvecCoursDirects, ...elevesAvecCoursGroupe]);
      setElevesSansCours(elevesRes.data.filter((e) => !tousElevesAvecCours.has(e.id)));

      // ── ACTIVITÉ PAR ENSEIGNANT ──
      if (isAdmin && enseignantsRes.data.length > 0) {
        const ensActivite = enseignantsRes.data.map((ens) => ({
          ...ens,
          nbCours: coursRes.data.filter((c) => c.enseignant_id === ens.id && !c.chapitre_termine).length,
        })).sort((a, b) => b.nbCours - a.nbCours);
        setActiviteEnseignants(ensActivite);
      }

      // ── GRAPHIQUES ──
      const months = getLast12Months();
      const elevesMap = Object.fromEntries(elevesStats.data.map((d) => [d.mois, parseInt(d.total)]));
      const coursMap = Object.fromEntries(coursStats.data.map((d) => [d.mois, parseInt(d.total)]));

      setElevesParMois({
        labels: months.map((m) => m.label),
        datasets: { label: "Nouveaux élèves", data: months.map((m) => elevesMap[m.key] || 0) },
      });
      setCoursParMois({
        labels: months.map((m) => m.label),
        datasets: { label: "Cours planifiés", data: months.map((m) => coursMap[m.key] || 0) },
      });

    } catch (error) {
      console.error("Erreur dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {objectifs && (() => {
          const saison = objectifs.saison;
          const realiseInsc = calcRealise(elevesData, saison);
          const realiseEns = isAdmin ? calcRealise(enseignantsData, saison) : 0;
          const pctInsc = objectifs.objectif_inscriptions > 0 ? Math.round((realiseInsc / objectifs.objectif_inscriptions) * 100) : null;
          const pctEns = objectifs.objectif_enseignants > 0 ? Math.round((realiseEns / objectifs.objectif_enseignants) * 100) : null;
          const couleur = (pct) => pct === null ? "#9e9e9e" : (pct >= 100 ? "#2e7d32" : (pct >= 60 ? "#f57c00" : "#c62828"));
          const Barre = ({ label, realise, obj, pct }) => (
            <MDBox sx={{ flex: 1, minWidth: 220 }}>
              <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#344767" }}>{label}</MDTypography>
                <MDTypography variant="button" fontWeight="bold" sx={{ color: couleur(pct) }}>
                  {realise} / {obj}{pct !== null ? ` (${pct}%)` : ""}
                </MDTypography>
              </MDBox>
              <MDBox sx={{ height: 8, borderRadius: 4, background: "#f0f0f0", overflow: "hidden" }}>
                <MDBox sx={{
                  height: "100%",
                  width: pct !== null ? Math.min(pct, 100) + "%" : "0%",
                  background: couleur(pct),
                  transition: "width 0.4s ease",
                }} />
              </MDBox>
            </MDBox>
          );
          return (
            <MDBox mb={3} p={2.5} sx={{ background: "#fff", borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
              <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#344767", mb: 2 }}>
                🎯 Objectifs saison {saison}-{saison + 1}
              </MDTypography>
              <MDBox display="flex" gap={3} flexWrap="wrap">
                <Barre label="Inscriptions" realise={realiseInsc} obj={objectifs.objectif_inscriptions} pct={pctInsc} />
                {isAdmin && <Barre label="Enseignants" realise={realiseEns} obj={objectifs.objectif_enseignants} pct={pctEns} />}
              </MDBox>
            </MDBox>
          );
        })()}


        {/* ── BANNIÈRE VILLE ── */}
        <MDBox mb={3} p={2.5} sx={{
          background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(21,101,192,0.3)",
          display: "flex", alignItems: "center", gap: 2,
        }}>
          <MDBox sx={{
            width: 48, height: 48, borderRadius: "12px",
            background: "rgba(255,255,255,0.2)",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            <Icon sx={{ color: "#fff", fontSize: 28 }}>location_city</Icon>
          </MDBox>
          <MDBox>
            <MDTypography variant="h5" color="white" fontWeight="bold">
              Solution de Gestion de cours de la ville de Nîmes
            </MDTypography>
            <MDTypography variant="caption" color="white" sx={{ opacity: 0.8 }}>
              Bienvenue sur votre espace de gestion des cours de religion
            </MDTypography>
          </MDBox>
        </MDBox>

        {/* ── STATISTIQUES PRINCIPALES ── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5} sx={{ cursor: "pointer" }} onClick={() => navigate("/eleves")}>
              <ComplexStatisticsCard color="info" icon="school" title="Élèves"
                count={loading ? "..." : stats.eleves}
                percentage={{ color: "info", amount: "", label: "élèves enregistrés" }} />
            </MDBox>
          </Grid>
          {isAdmin && (
            <Grid item xs={12} md={6} lg={3}>
              <MDBox mb={1.5} sx={{ cursor: "pointer" }} onClick={() => navigate("/enseignants")}>
                <ComplexStatisticsCard color="dark" icon="person" title="Enseignants"
                  count={loading ? "..." : stats.enseignants}
                  percentage={{ color: "dark", amount: "", label: "enseignants actifs" }} />
              </MDBox>
            </Grid>
          )}
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5} sx={{ cursor: "pointer" }} onClick={() => navigate("/cours")}>
              <ComplexStatisticsCard color="success" icon="menu_book" title="Cours"
                count={loading ? "..." : stats.cours}
                percentage={{ color: "success", amount: "", label: "cours planifiés" }} />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5} sx={{ cursor: "pointer" }} onClick={() => navigate("/livres")}>
              <ComplexStatisticsCard color="warning" icon="library_books" title="Livres"
                count={loading ? "..." : stats.livres}
                percentage={{ color: "warning", amount: "", label: "livres disponibles" }} />
            </MDBox>
          </Grid>
        </Grid>

        {/* ── GRAPHIQUES ── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={6}>
            <ReportsLineChart color="info" title="Nouveaux élèves par mois"
              description="Évolution des inscriptions sur 12 mois"
              date="mis à jour maintenant" chart={elevesParMois} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ReportsBarChart color="success" title="Cours planifiés par mois"
              description="Nombre de cours sur 12 mois"
              date="mis à jour maintenant" chart={coursParMois} />
          </Grid>
        </Grid>

        {/* ── NOUVEAUX ÉLÈVES DU MOIS ── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12}>
            <MDBox sx={{
              background: "#fff", borderRadius: "16px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.06)", overflow: "hidden",
            }}>
              <MDBox px={3} py={2.5} sx={{
                background: "linear-gradient(135deg, #00796b 0%, #009688 100%)",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                flexWrap: "wrap", gap: 1,
              }}>
                <MDBox>
                  <MDTypography variant="h6" color="white" fontWeight="bold">
                    🌟 Nouveaux élèves du mois
                  </MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                    {getCurrentMonthLabel()} — {nouveauxEleves.length} nouvelle{nouveauxEleves.length > 1 ? "s" : ""} inscription{nouveauxEleves.length > 1 ? "s" : ""}
                  </MDTypography>
                </MDBox>
                {nouveauxEleves.length > 0 && (
                  <MDBox sx={{
                    background: "rgba(255,255,255,0.2)", borderRadius: "12px", px: 2, py: 0.5,
                  }}>
                    <MDTypography variant="h5" color="white" fontWeight="bold">
                      {nouveauxEleves.length}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
              <MDBox p={3}>
                {loading ? (
                  <MDTypography variant="body2" color="text">Chargement...</MDTypography>
                ) : nouveauxEleves.length === 0 ? (
                  <MDBox display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
                    <Icon sx={{ fontSize: 48, color: "#e0e0e0" }}>person_add</Icon>
                    <MDTypography variant="body2" color="text">
                      Aucune nouvelle inscription ce mois-ci
                    </MDTypography>
                  </MDBox>
                ) : (
                  <Grid container spacing={1.5}>
                    {nouveauxEleves.map((eleve, index) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={eleve.id}>
                        <MDBox
                          onClick={() => navigate(`/eleves/${eleve.id}`)}
                          display="flex" alignItems="center" gap={1.5}
                          py={1.2} px={1.5}
                          sx={{
                            borderRadius: "12px", cursor: "pointer",
                            background: index % 2 === 0 ? "#e0f2f1" : "#f5fffe",
                            border: "1px solid #b2dfdb", transition: "all 0.2s",
                            "&:hover": { background: "#b2dfdb", transform: "translateY(-2px)", boxShadow: "0 4px 12px rgba(0,121,107,0.2)" },
                          }}>
                          <MDBox sx={{
                            width: 40, height: 40, borderRadius: "50%", flexShrink: 0,
                            background: "#00796b", color: "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontWeight: 700, fontSize: 14, overflow: "hidden",
                          }}>
                            {eleve.photo ? (
                              <img src={`${eleve.photo}`} alt=""
                                style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            ) : (
                              `${eleve.prenom?.[0] || ""}${eleve.nom?.[0] || ""}`
                            )}
                          </MDBox>
                          <MDBox flex={1} sx={{ overflow: "hidden" }}>
                            <MDTypography variant="caption" fontWeight="bold"
                              sx={{ color: "#344767", display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              {eleve.prenom} {eleve.nom}
                            </MDTypography>
                            {eleve.ville && (
                              <MDTypography variant="caption" color="text" display="block">
                                📍 {eleve.ville}
                              </MDTypography>
                            )}
                            <MDTypography variant="caption" sx={{ color: "#00796b", fontSize: 10, fontWeight: 600 }}>
                              inscrit le {new Date(eleve.createdAt).toLocaleDateString("fr-FR")}
                            </MDTypography>
                          </MDBox>
                          <MDBox sx={{
                            flexShrink: 0, background: "#00796b", color: "#fff",
                            borderRadius: "8px", px: 1, py: 0.3,
                            fontSize: 10, fontWeight: 700,
                          }}>
                            NEW
                          </MDBox>
                        </MDBox>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>

        <Grid container spacing={3} mb={3}>

          {/* ── ÉLÈVES SANS COURS ── */}
          <Grid item xs={12} md={6}>
            <MDBox sx={{
              background: "#fff", borderRadius: "16px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.06)", overflow: "hidden", height: "100%",
            }}>
              <MDBox px={3} py={2.5} sx={{ background: "linear-gradient(135deg, #c62828, #e53935)" }}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDBox>
                    <MDTypography variant="h6" color="white" fontWeight="bold">
                      ⚠️ Élèves sans cours
                    </MDTypography>
                    <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                      {elevesSansCours.length} élève{elevesSansCours.length > 1 ? "s" : ""} non affecté{elevesSansCours.length > 1 ? "s" : ""}
                    </MDTypography>
                  </MDBox>
                  {elevesSansCours.length > 0 && (
                    <MDBox sx={{
                      width: 36, height: 36, borderRadius: "50%",
                      background: "rgba(255,255,255,0.2)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <MDTypography variant="h6" color="white" fontWeight="bold">
                        {elevesSansCours.length}
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              </MDBox>
              <MDBox p={3}>
                {loading ? (
                  <MDTypography variant="body2" color="text">Chargement...</MDTypography>
                ) : elevesSansCours.length === 0 ? (
                  <MDBox display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
                    <Icon sx={{ fontSize: 40, color: "#e0e0e0" }}>check_circle</Icon>
                    <MDTypography variant="caption" color="text" textAlign="center">
                      Tous les élèves ont un cours affecté ! 🎉
                    </MDTypography>
                  </MDBox>
                ) : (
                  <MDBox sx={{ maxHeight: 280, overflowY: "auto" }}>
                    {elevesSansCours.map((eleve) => (
                      <MDBox key={eleve.id}
                        onClick={() => navigate(`/eleves/${eleve.id}`)}
                        display="flex" alignItems="center" gap={2}
                        py={1} px={1.5} mb={1}
                        sx={{
                          borderRadius: "10px", cursor: "pointer",
                          background: "#fff5f5", border: "1px solid #ffcdd2",
                          transition: "all 0.2s",
                          "&:hover": { background: "#ffebee", transform: "translateX(4px)" },
                        }}>
                        <MDBox sx={{
                          width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                          background: "#c62828", color: "#fff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 700, fontSize: 13, overflow: "hidden",
                        }}>
                          {eleve.photo ? (
                            <img src={`${eleve.photo}`} alt=""
                              style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            `${eleve.prenom?.[0] || ""}${eleve.nom?.[0] || ""}`
                          )}
                        </MDBox>
                        <MDBox flex={1}>
                          <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#344767" }}>
                            {eleve.prenom} {eleve.nom}
                          </MDTypography>
                          {eleve.ville && (
                            <MDTypography variant="caption" color="text" display="block">
                              📍 {eleve.ville}
                            </MDTypography>
                          )}
                        </MDBox>
                        <MDBox
                          onClick={(e) => { e.stopPropagation(); navigate(`/cours/nouveau?eleve=${eleve.id}`); }}
                          sx={{
                            padding: "4px 10px", borderRadius: "8px",
                            background: "#c62828", color: "#fff",
                            fontSize: 11, fontWeight: 600, cursor: "pointer", flexShrink: 0,
                            "&:hover": { background: "#b71c1c" },
                          }}>
                          + Cours
                        </MDBox>
                      </MDBox>
                    ))}
                  </MDBox>
                )}
              </MDBox>
            </MDBox>
          </Grid>

          {/* ── ACTIVITÉ PAR ENSEIGNANT ── */}
          {isAdmin && (
            <Grid item xs={12} md={6}>
              <MDBox sx={{
                background: "#fff", borderRadius: "16px",
                boxShadow: "0 4px 24px rgba(0,0,0,0.06)", overflow: "hidden", height: "100%",
              }}>
                <MDBox px={3} py={2.5} sx={{ background: "linear-gradient(135deg, #6a1b9a, #7b1fa2)" }}>
                  <MDTypography variant="h6" color="white" fontWeight="bold">
                    👨‍🏫 Activité par enseignant
                  </MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                    Nombre de cours par enseignant
                  </MDTypography>
                </MDBox>
                <MDBox p={3}>
                  {loading ? (
                    <MDTypography variant="body2" color="text">Chargement...</MDTypography>
                  ) : activiteEnseignants.length === 0 ? (
                    <MDBox display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
                      <Icon sx={{ fontSize: 40, color: "#e0e0e0" }}>person</Icon>
                      <MDTypography variant="caption" color="text">Aucun enseignant</MDTypography>
                    </MDBox>
                  ) : (
                    activiteEnseignants.map((ens) => {
                      const maxCours = activiteEnseignants[0]?.nbCours || 1;
                      const pct = Math.round((ens.nbCours / maxCours) * 100);
                      return (
                        <MDBox key={ens.id}
                          onClick={() => navigate(`/enseignants/${ens.id}`)}
                          display="flex" alignItems="center" gap={2}
                          py={1.2} px={1.5} mb={1}
                          sx={{
                            borderRadius: "10px", cursor: "pointer",
                            background: "#fdf8ff", border: "1px solid #f3e5f5",
                            transition: "all 0.2s",
                            "&:hover": { background: "#f3e5f5", transform: "translateX(4px)" },
                          }}>
                          <MDBox sx={{
                            width: 38, height: 38, borderRadius: "50%", flexShrink: 0,
                            background: "#6a1b9a", color: "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontWeight: 700, fontSize: 13, overflow: "hidden",
                          }}>
                            {ens.photo ? (
                              <img src={`${ens.photo}`} alt=""
                                style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            ) : (
                              `${ens.prenom?.[0] || ""}${ens.nom?.[0] || ""}`
                            )}
                          </MDBox>
                          <MDBox flex={1}>
                            <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                              <MDTypography variant="caption" fontWeight="bold" sx={{ color: "#344767" }}>
                                {ens.prenom} {ens.nom}
                              </MDTypography>
                              <MDBox sx={{
                                padding: "1px 8px", borderRadius: "10px",
                                background: "#f3e5f5", color: "#6a1b9a",
                                fontSize: 11, fontWeight: 700,
                              }}>
                                {ens.nbCours} cours
                              </MDBox>
                            </MDBox>
                            <MDBox sx={{ background: "#f5f5f5", borderRadius: "10px", height: 8, overflow: "hidden" }}>
                              <MDBox sx={{
                                width: `${pct}%`, height: "100%",
                                background: "linear-gradient(135deg, #6a1b9a, #9c27b0)",
                                borderRadius: "10px", transition: "width 0.5s ease",
                              }} />
                            </MDBox>
                          </MDBox>
                        </MDBox>
                      );
                    })
                  )}
                </MDBox>
              </MDBox>
            </Grid>
          )}
        </Grid>

        {/* ── DERNIERS COURS ── */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12}>
            <MDBox sx={{
              background: "#fff", borderRadius: "16px",
              boxShadow: "0 4px 24px rgba(0,0,0,0.06)", overflow: "hidden",
            }}>
              <MDBox px={3} py={2.5} sx={{
                background: "linear-gradient(135deg, #2e7d32, #388e3c)",
                display: "flex", justifyContent: "space-between", alignItems: "center",
              }}>
                <MDBox>
                  <MDTypography variant="h6" color="white" fontWeight="bold">
                    Derniers cours planifiés
                  </MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                    Les 5 cours les plus récents
                  </MDTypography>
                </MDBox>
                <MDBox onClick={() => navigate("/cours")} sx={{
                  padding: "4px 12px", borderRadius: "8px",
                  background: "rgba(255,255,255,0.2)", cursor: "pointer",
                  color: "#fff", fontSize: 12, fontWeight: 600,
                  "&:hover": { background: "rgba(255,255,255,0.3)" },
                }}>
                  Voir tout
                </MDBox>
              </MDBox>
              <MDBox p={3}>
                {loading ? (
                  <MDTypography variant="body2" color="text">Chargement...</MDTypography>
                ) : derniersCours.length === 0 ? (
                  <MDBox display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
                    <Icon sx={{ fontSize: 48, color: "#e0e0e0" }}>menu_book</Icon>
                    <MDTypography variant="body2" color="text">
                      Aucun cours planifié pour l&apos;instant
                    </MDTypography>
                  </MDBox>
                ) : (
                  <Grid container spacing={1.5}>
                    {derniersCours.map((cours, index) => {
                      const m = MATIERES[cours.matiere] || { label: cours.matiere, color: "#333", bg: "#f5f5f5" };
                      return (
                        <Grid item xs={12} md={6} key={cours.id}>
                          <MDBox
                            onClick={() => navigate(`/cours/${cours.id}`)}
                            display="flex" alignItems="center" justifyContent="space-between"
                            py={1.5} px={2}
                            sx={{
                              background: index % 2 === 0 ? "#f8f8f8" : "#fff",
                              borderRadius: "10px", cursor: "pointer",
                              border: "1px solid #f0f0f0", transition: "all 0.2s",
                              "&:hover": { background: m.bg, borderColor: `${m.color}33` },
                            }}>
                            <MDBox display="flex" alignItems="center" gap={2}>
                              <MDBox sx={{
                                width: 36, height: 36, borderRadius: "10px",
                                background: m.bg, color: m.color,
                                display: "flex", alignItems: "center", justifyContent: "center",
                              }}>
                                <Icon sx={{ fontSize: 18 }}>menu_book</Icon>
                              </MDBox>
                              <MDBox>
                                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#344767" }}>
                                  {cours.eleve?.prenom} {cours.eleve?.nom}
                                </MDTypography>
                                <MDTypography variant="caption" color="text" display="block">
                                  {m.label} · {cours.enseignant?.prenom} {cours.enseignant?.nom}
                                </MDTypography>
                              </MDBox>
                            </MDBox>
                            <MDBox textAlign="right">
                              {formatPlanning(cours) && (
                                <MDTypography variant="caption" color="text" display="block">
                                  🕐 {formatPlanning(cours)}
                                </MDTypography>
                              )}
                              <MDBox sx={{
                                display: "inline-block", padding: "2px 8px",
                                borderRadius: "8px", background: m.bg, color: m.color,
                                fontSize: 11, fontWeight: 600, mt: 0.5,
                              }}>
                                {cours.chapitres?.length || 0} chap.
                              </MDBox>
                            </MDBox>
                          </MDBox>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>

      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
